/**
 * Magnet snapping — pull a price to the nearest OHLC value of a bar.
 *
 * This MIRRORS klinecharts' own implementation in `OverlayView._coordinateToPoint`
 * (dist/index.esm.js:8647-8700), deliberately and exactly.
 *
 * Why duplicate it: klinecharts applies magnet ONLY when resolving a drawing
 * point — `_coordinateToPoint` is called from four sites, all of them drawing
 * or dragging an overlay. The crosshair is never snapped, so with the magnet on
 * the cursor slides freely and only the finished point lands on the bar. That
 * reads as "the magnet does nothing", because the one piece of feedback the
 * user is actually watching never moves.
 *
 * To snap the crosshair we need the same arithmetic outside klinecharts. It has
 * to agree with klinecharts EXACTLY — a crosshair sitting on the high while the
 * placed point lands on the close would be worse than no feedback at all — so
 * this is a transcription, not a reinterpretation. Keep it that way.
 */

export type MagnetMode = 'normal' | 'weak_magnet' | 'strong_magnet'

export interface MagnetBar {
  open: number
  high: number
  low: number
  close: number
}

/**
 * `weak` only grabs a value the cursor is already close to, so the pointer
 * stays free in open space; `strong` always grabs. Note the asymmetry, which is
 * klinecharts' own: the sensitivity band applies only OUTSIDE the bar's range.
 * Once the cursor is between low and high, both modes snap unconditionally to
 * whichever of open/close/high/low is nearest.
 *
 * @param value          price under the cursor
 * @param bar            the bar under the cursor
 * @param mode           magnet mode
 * @param sensitivity    price-space equivalent of `modeSensitivity` pixels —
 *                       the caller converts, since only it knows the y-scale
 * @returns the snapped price, or `value` unchanged
 */
export function snapValueToBar(
  value: number,
  bar: MagnetBar,
  mode: MagnetMode,
  sensitivity: number,
): number {
  if (mode === 'normal') return value

  if (value > bar.high) {
    if (mode === 'weak_magnet') {
      // klinecharts compares against the price `modeSensitivity` px ABOVE the
      // high, so the band sits between the high and that value.
      return value < bar.high + sensitivity ? bar.high : value
    }
    return bar.high
  }

  if (value < bar.low) {
    if (mode === 'weak_magnet') {
      return value > bar.low - sensitivity ? bar.low : value
    }
    return bar.low
  }

  // Inside the bar's range: snap to the nearest of the body edges and wicks.
  const max = Math.max(bar.open, bar.close)
  const min = Math.min(bar.open, bar.close)
  if (value > max) return value - max < bar.high - value ? max : bar.high
  if (value < min) return value - bar.low < min - value ? bar.low : min
  return max - value < value - min ? max : min
}
